import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, User, Award, Calendar, BarChart3, X, Check } from 'lucide-react'
import { useState } from 'react'
import api from '../../lib/api'
import { Badge, Accordion } from '../../components/common'

export const Route = createFileRoute('/dashboard/team-members/$id')({
  component: TeamMemberShowComponent,
})

interface TeamMemberDetail {
  id: number
  user_id: number
  team_id: number
  role: string
  status: string
  user: {
    id: number
    email: string
    role: string
    first_name?: string
    last_name?: string
    full_name?: string
    skills?: Array<{
      id: number
      name: string
      category: string
    }>
    user_skills?: Array<{
      id: number
      skill_id: number
      proficiency_level: number
      skill: {
        id: number
        name: string
        category: string
        description?: string
      }
    }>
  }
  team: {
    id: number
    name: string
  }
}

interface Appointment {
  id: number
  date: string
  start_time: string
  end_time: string
  status: string
  notes?: string
  cancelled_notes?: string
  completed_notes?: string
  customer: {
    id: number
    name: string
    email: string
    phone?: string
  }
  skill: {
    id: number
    name: string
    category: string
  }
}

function TeamMemberShowComponent() {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  const [openSection, setOpenSection] = useState<string>('details')
  const [calendarFilter, setCalendarFilter] = useState<'upcoming' | 'completed' | 'cancelled'>('upcoming')
  const [cancellingAppointment, setCancellingAppointment] = useState<number | null>(null)
  const [cancelNotes, setCancelNotes] = useState('')
  const [completingAppointment, setCompletingAppointment] = useState<number | null>(null)
  const [completeNotes, setCompleteNotes] = useState('')

  // Fetch team member details
  const { data: memberData, isLoading: memberLoading, error: memberError } = useQuery({
    queryKey: ['teamMember', id],
    queryFn: async () => {
      const response = await api.get<{ team_member: TeamMemberDetail }>(`/team_members/${id}`)
      return response.data
    },
  })

  // Fetch appointments for this team member
  const { data: appointmentsData, isLoading: appointmentsLoading } = useQuery({
    queryKey: ['teamMemberAppointments', id],
    queryFn: async () => {
      const response = await api.get<{ appointments: Appointment[] }>('/appointments', {
        params: {
          team_member_id: memberData?.team_member.id,
          start_date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days ago
          end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days ahead
        },
      })
      return response.data
    },
    enabled: !!memberData,
  })

  const member = memberData?.team_member
  const appointments = appointmentsData?.appointments || []
  
  // Cancel appointment mutation
  const cancelAppointmentMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      await api.patch(`/appointments/${id}/update_status`, {
        status: 'cancelled',
        cancelled_notes: notes
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMemberAppointments', id] })
      setCancellingAppointment(null)
      setCancelNotes('')
    },
  })
  
  // Complete appointment mutation
  const completeAppointmentMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      await api.patch(`/appointments/${id}/update_status`, {
        status: 'completed',
        completed_notes: notes
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMemberAppointments', id] })
      setCompletingAppointment(null)
      setCompleteNotes('')
    },
  })

  // Calculate statistics
  const stats = {
    total: appointments.length,
    completed: appointments.filter(a => a.status === 'completed').length,
    upcoming: appointments.filter(a => a.status === 'confirmed' || a.status === 'scheduled' || a.status === 'pending').length,
    cancelled: appointments.filter(a => a.status === 'cancelled').length,
  }

  // Filter appointments based on selected status
  const filteredAppointments = appointments.filter(apt => {
    if (calendarFilter === 'upcoming') {
      return apt.status === 'confirmed' || apt.status === 'scheduled' || apt.status === 'pending'
    } else if (calendarFilter === 'completed') {
      return apt.status === 'completed'
    } else {
      return apt.status === 'cancelled'
    }
  })
  
  // Group appointments by date for calendar view
  const appointmentsByDate = filteredAppointments.reduce((acc, apt) => {
    const date = apt.date
    if (!acc[date]) acc[date] = []
    acc[date].push(apt)
    return acc
  }, {} as Record<string, Appointment[]>)
  
  const handleCancelAppointment = (appointmentId: number) => {
    setCancellingAppointment(appointmentId)
  }
  
  const confirmCancel = () => {
    if (cancellingAppointment) {
      cancelAppointmentMutation.mutate({
        id: cancellingAppointment,
        notes: cancelNotes
      })
    }
  }
  
  const handleCompleteAppointment = (appointmentId: number) => {
    setCompletingAppointment(appointmentId)
  }
  
  const confirmComplete = () => {
    if (completingAppointment) {
      completeAppointmentMutation.mutate({
        id: completingAppointment,
        notes: completeNotes
      })
    }
  }

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? '' : section)
  }

  if (memberLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  if (memberError || !member) {
    return (
      <div className="alert alert-error">
        <span>Error loading team member details</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/team-members" className="btn btn-ghost btn-sm">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{member.user.full_name || member.user.email}</h1>
          <p className="text-base-content/70 mt-1">Team Member Details</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stat bg-base-100 rounded-lg shadow-xl">
          <div className="stat-title">Total Appointments</div>
          <div className="stat-value text-primary">{stats.total}</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow-xl">
          <div className="stat-title">Completed</div>
          <div className="stat-value text-success">{stats.completed}</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow-xl">
          <div className="stat-title">Upcoming</div>
          <div className="stat-value text-info">{stats.upcoming}</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow-xl">
          <div className="stat-title">Cancelled</div>
          <div className="stat-value text-warning">{stats.cancelled}</div>
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="space-y-2">
        {/* Details & Contact */}
        <Accordion
          title="Details & Contact"
          icon={<User className="w-5 h-5" />}
          open={openSection === 'details'}
          onToggle={() => toggleSection('details')}
          defaultOpen
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div>
                <h3 className="font-semibold mb-2">Personal Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-base-content/70">Full Name:</span>
                    <p className="font-medium">{member.user.full_name || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-base-content/70">Email:</span>
                    <p className="font-medium">{member.user.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-base-content/70">Role:</span>
                    <p className="font-medium capitalize">{member.user.role}</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Team Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-base-content/70">Team:</span>
                    <p className="font-medium">{member.team.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-base-content/70">Team Role:</span>
                    <p className="font-medium capitalize">{member.role}</p>
                  </div>
                  <div>
                    <span className="text-sm text-base-content/70">Status:</span>
                    <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
                      {member.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
        </Accordion>

        {/* Skills & Proficiency */}
        <Accordion
          title="Skills & Proficiency"
          icon={<Award className="w-5 h-5" />}
          open={openSection === 'skills'}
          onToggle={() => toggleSection('skills')}
        >
            <div className="pt-4">
              {member.user.user_skills && member.user.user_skills.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {member.user.user_skills.map((userSkill) => (
                    <div key={userSkill.id} className="card bg-base-100 shadow">
                      <div className="card-body p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold">{userSkill.skill.name}</h4>
                            <p className="text-sm text-base-content/70">{userSkill.skill.category}</p>
                          </div>
                          <div className="text-right">
                            <div className="badge badge-accent text-white font-semibold">
                              ${Number(userSkill.rate).toFixed(2)}/30min
                            </div>
                          </div>
                        </div>
                        {userSkill.skill.description && (
                          <p className="text-xs text-base-content/60 mt-1">{userSkill.skill.description}</p>
                        )}
                        <div className="mt-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Proficiency</span>
                            <span>{userSkill.proficiency_level}/10</span>
                          </div>
                          <progress
                            className="progress progress-accent w-full"
                            value={userSkill.proficiency_level}
                            max="10"
                          ></progress>
                        </div>
                        <div className="mt-2 text-xs text-base-content/60">
                          <span>Example: 1.5 hours (3 × 30min) = ${(Number(userSkill.rate) * 3).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base-content/50">No skills assigned</p>
              )}
            </div>
        </Accordion>

        {/* Calendar & Schedule */}
        <Accordion
          title="Calendar & Schedule"
          icon={<Calendar className="w-5 h-5" />}
          open={openSection === 'calendar'}
          onToggle={() => toggleSection('calendar')}
        >
            <div className="pt-4">
              {/* Filter Buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  className={`btn btn-sm ${calendarFilter === 'upcoming' ? 'btn-accent text-white' : 'btn-ghost'}`}
                  onClick={() => setCalendarFilter('upcoming')}
                >
                  Upcoming ({stats.upcoming})
                </button>
                <button
                  className={`btn btn-sm ${calendarFilter === 'completed' ? 'btn-accent text-white' : 'btn-ghost'}`}
                  onClick={() => setCalendarFilter('completed')}
                >
                  Completed ({stats.completed})
                </button>
                <button
                  className={`btn btn-sm ${calendarFilter === 'cancelled' ? 'btn-accent text-white' : 'btn-ghost'}`}
                  onClick={() => setCalendarFilter('cancelled')}
                >
                  Cancelled ({stats.cancelled})
                </button>
              </div>
              
              {appointmentsLoading ? (
                <div className="flex justify-center py-8">
                  <span className="loading loading-spinner"></span>
                </div>
              ) : filteredAppointments.length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(appointmentsByDate)
                    .sort(([dateA], [dateB]) => {
                      // For upcoming: earliest first, for others: latest first
                      return calendarFilter === 'upcoming' 
                        ? dateA.localeCompare(dateB) 
                        : dateB.localeCompare(dateA)
                    })
                    .slice(0, 30) // Show last 30 dates
                    .map(([date, apts]) => (
                      <div key={date} className="card bg-base-100 shadow">
                        <div className="card-body p-4">
                          <h4 className="font-semibold">
                            {new Date(date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </h4>
                          <div className="space-y-2 mt-2">
                            {apts.map((apt) => (
                              <div
                                key={apt.id}
                                className="flex items-center justify-between p-2 bg-base-200 rounded"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {apt.start_time} - {apt.end_time}
                                    </span>
                                    <Badge
                                      size="sm"
                                      variant={
                                        apt.status === 'completed' ? 'success' :
                                        apt.status === 'confirmed' ? 'info' :
                                        apt.status === 'cancelled' ? 'error' :
                                        'warning'
                                      }
                                    >
                                      {apt.status}
                                    </Badge>
                                  </div>
                                  <div className="text-sm text-base-content/70">
                                    {apt.customer.name} - {apt.skill.name}
                                  </div>
                                  {apt.status === 'cancelled' && apt.cancelled_notes ? (
                                    <div className="text-xs text-error mt-1">
                                      Cancelled: {apt.cancelled_notes}
                                    </div>
                                  ) : apt.status === 'completed' && apt.completed_notes ? (
                                    <div className="text-xs text-success mt-1">
                                      Completed: {apt.completed_notes}
                                    </div>
                                  ) : apt.notes ? (
                                    <div className="text-xs text-base-content/50 mt-1">
                                      Note: {apt.notes}
                                    </div>
                                  ) : null}
                                </div>
                                {calendarFilter === 'upcoming' && (
                                  <div className="flex gap-2">
                                    <button
                                      className="btn btn-sm btn-ghost text-success"
                                      onClick={() => handleCompleteAppointment(apt.id)}
                                      title="Complete Appointment"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-ghost text-error"
                                      onClick={() => handleCancelAppointment(apt.id)}
                                      title="Cancel Appointment"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-base-content/50">No appointments found</p>
              )}
            </div>
        </Accordion>

        {/* Statistics */}
        <Accordion
          title="Statistics & Performance"
          icon={<BarChart3 className="w-5 h-5" />}
          open={openSection === 'statistics'}
          onToggle={() => toggleSection('statistics')}
        >
            <div className="pt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-base-content/70">Total Sessions</div>
                  <div className="text-2xl font-bold">{stats.total}</div>
                </div>
                <div>
                  <div className="text-sm text-base-content/70">Completion Rate</div>
                  <div className="text-2xl font-bold">
                    {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-base-content/70">Active Skills</div>
                  <div className="text-2xl font-bold">{member.user.user_skills?.length || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-base-content/70">Avg Proficiency</div>
                  <div className="text-2xl font-bold">
                    {member.user.user_skills && member.user.user_skills.length > 0
                      ? (
                          member.user.user_skills.reduce((sum, us) => sum + us.proficiency_level, 0) /
                          member.user.user_skills.length
                        ).toFixed(1)
                      : '0'}
                    /5
                  </div>
                </div>
              </div>
            </div>
        </Accordion>
      </div>

      {/* Cancel Appointment Modal */}
      {cancellingAppointment && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Cancel Appointment</h3>
            <p className="mb-4">Are you sure you want to cancel this appointment?</p>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Cancellation Notes (optional)</span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                placeholder="Enter reason for cancellation..."
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
              />
            </div>

            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setCancellingAppointment(null)
                  setCancelNotes('')
                }}
              >
                Keep Appointment
              </button>
              <button
                type="button"
                className="btn btn-error text-white"
                onClick={confirmCancel}
                disabled={cancelAppointmentMutation.isPending}
              >
                {cancelAppointmentMutation.isPending ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => {
              setCancellingAppointment(null)
              setCancelNotes('')
            }}>close</button>
          </form>
        </dialog>
      )}

      {/* Complete Appointment Modal */}
      {completingAppointment && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Complete Appointment</h3>
            <p className="mb-4">Mark this appointment as completed?</p>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Completion Notes (optional)</span>
              </label>
              <textarea
                className="textarea textarea-bordered h-24"
                placeholder="Enter any notes about the session..."
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
              />
            </div>

            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setCompletingAppointment(null)
                  setCompleteNotes('')
                }}
              >
                Go Back
              </button>
              <button
                type="button"
                className="btn btn-success text-white"
                onClick={confirmComplete}
                disabled={completeAppointmentMutation.isPending}
              >
                {completeAppointmentMutation.isPending ? 'Completing...' : 'Mark as Completed'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => {
              setCompletingAppointment(null)
              setCompleteNotes('')
            }}>close</button>
          </form>
        </dialog>
      )}
    </div>
  )
}
